import { useAuth0 } from "ct-auth"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  addOrganization,
  assignOrg,
  deleteOrganization,
  deleteOrgMember,
  getExercises,
  getManagedOrgs,
  getOrg,
  getOrgMembers,
  getOrgStats,
  getOrgs,
  getScenarios,
  inviteOrgTrainer,
  inviteOrgUser,
  inviteSponsor,
  releaseOrg,
  resendMemberInvite,
  restoreOrgMember,
  transferPrimarySponsorship,
  updateOrg,
  updateScenarioOrgAccess,
  updateSponsor,
} from "./data"
import { isTrainer } from "./authUtils"

// TanStack Query hooks for the partner Organizations area. The server enforces managed
// scope (own org + managed children) on every endpoint below, so the client never sends
// child org ids — it simply reads what the caller is entitled to and renders it.
export const ORGANIZATION_QUERY_KEYS = {
  MANAGED_ORGS: (userSub) => [`MANAGED_ORGS-${userSub}`],
  MANAGED_ORGS_LIST: (userSub) => [`MANAGED_ORGS_LIST-${userSub}`],
  ORG_STATS: (userSub, orgId) => [`ORG_STATS-${userSub}-${orgId}`],
  ORG_DETAIL: (userSub, orgId) => [`ORG_DETAIL-${userSub}-${orgId}`],
  ORG_MEMBERS: (userSub, orgId) => [`ORG_MEMBERS-${userSub}-${orgId}`],
  MANAGED_EXERCISES: (userSub) => [`MANAGED_EXERCISES-${userSub}`],
  MANAGED_SCENARIOS: (userSub) => [`MANAGED_SCENARIOS-${userSub}`],
  MANAGED_ORG_IDS: (userSub) => [`MANAGED_ORG_IDS-${userSub}`],
}

export const useOrganizationApis = Object.freeze({
  // Orgs the caller can see. For a partner this is its own org + managed children
  // (server-enforced managed scope on /orgs). The page filters to children for display.
  MANAGED_ORGS: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS(user?.sub),
      queryFn: async () => {
        const accessToken = await getAccessTokenSilently()
        return (await getOrgs(accessToken, user?.sub)) ?? []
      },
      enabled: !!user?.sub,
      staleTime: 30000,
    })
  },

  ADD_ORG: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({
        id,
        displayName,
        sponsorName,
        sponsorEmail,
        inviteContactAsSponsor,
        companyURL,
        entitlements,
        isPartnerOrg,
        partnerGrantableEntitlements,
        analyzeWebsite,
      }) => {
        const accessToken = await getAccessTokenSilently()
        return await addOrganization(accessToken, user?.sub, {
          id,
          displayName,
          sponsorName,
          sponsorEmail,
          inviteContactAsSponsor,
          companyURL,
          entitlements,
          isPartnerOrg,
          partnerGrantableEntitlements,
          analyzeWebsite,
        })
      },
      onSuccess: () => {
        // A partner's grant list (and other org settings) are read from HOME_USER_ORG elsewhere.
        queryClient.invalidateQueries({ queryKey: ["HOME_USER_ORG"] })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS_LIST(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORG_IDS(user?.sub) })
      },
    })
  },

  // Exercises across the managed org set — the server aggregates child orgs for a partner,
  // so no org param is passed. Each game carries `org: { id, name }` for the org column.
  MANAGED_EXERCISES: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_EXERCISES(user?.sub),
      queryFn: async () => {
        const accessToken = await getAccessTokenSilently()
        return (await getExercises(accessToken, user?.sub, {})) ?? []
      },
      enabled: !!user?.sub,
      staleTime: 30000,
    })
  },

  // Scenarios across the managed org set (server scopes /tests by managed scope).
  MANAGED_SCENARIOS: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_SCENARIOS(user?.sub),
      queryFn: async () => {
        const accessToken = await getAccessTokenSilently()
        return (await getScenarios(accessToken, user?.sub)) ?? []
      },
      enabled: !!user?.sub,
      staleTime: 30000,
    })
  },

  // Fetches the partner-scoped org list from /managed-orgs (sponsor+manager only).
  // Distinct from MANAGED_ORGS which uses /orgs.
  // `enabled` accepts an optional caller override so a page guard (e.g. canManageOrgs)
  // can prevent the partner-scoped fetch from firing before the access check passes.
  MANAGED_ORGS_LIST: ({ enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS_LIST(user?.sub),
      queryFn: async () => {
        const accessToken = await getAccessTokenSilently()
        return (await getManagedOrgs(accessToken, user?.sub)) ?? []
      },
      enabled: !!user?.sub && enabled,
      staleTime: 30000,
    })
  },

  // Stats for a single org (total users, per-role split, sponsors list).
  // `enabled` accepts an optional caller override so a page guard (e.g. canManageOrgs)
  // can prevent the fetch from firing before the access check passes.
  ORG_STATS: (orgId, { enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, orgId),
      queryFn: async () => {
        const accessToken = await getAccessTokenSilently()
        return await getOrgStats(accessToken, user?.sub, { orgId })
      },
      enabled: !!user?.sub && !!orgId && enabled,
      staleTime: 30000,
    })
  },

  // Assign a customer org to a partner (manager only).
  ASSIGN_ORG: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ orgId, partnerOrgId }) => {
        const accessToken = await getAccessTokenSilently()
        return await assignOrg(accessToken, user?.sub, { orgId, partnerOrgId })
      },
      onSuccess: (_, { orgId }) => {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS_LIST(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORG_IDS(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, orgId) })
      },
    })
  },

  // Release a customer org from a partner (sponsor + manager).
  RELEASE_ORG: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ orgId }) => {
        const accessToken = await getAccessTokenSilently()
        return await releaseOrg(accessToken, user?.sub, { orgId })
      },
      onSuccess: (_, { orgId }) => {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS_LIST(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORG_IDS(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, orgId) })
      },
    })
  },

  // Hard-delete a customer org (manager only). `force` removes an EMPTY real org past the prod
  // ephemeral guard (server re-verifies manager + emptiness). Invalidates the org lists AND the
  // assume-role picker's GET_ORGS cache so a deleted org can't linger there (Issue B staleness).
  DELETE_ORG: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ id, force = false }) => {
        const accessToken = await getAccessTokenSilently()
        return await deleteOrganization(accessToken, user?.sub, { id, force })
      },
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS_LIST(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORG_IDS(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, id) })
        // Assume-role picker reads GET_ORGS (generationUtil key) — drop it so the deleted org
        // can't reappear there.
        queryClient.invalidateQueries({ queryKey: [`GET_ORGS-${user?.sub}`] })
      },
    })
  },

  // Update org metadata including partner-specific fields (companyURL, entitlements, archived, managingOrgId).
  UPDATE_ORG: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({
        id,
        displayName,
        sponsorName,
        sponsorEmail,
        variables,
        logoURL,
        brandColor,
        gameplayPreferences,
        companyURL,
        entitlements,
        archived,
        managingOrgId,
        isPartnerOrg,
        inheritPartnerBranding,
        showManagedByLabel,
        showPoweredByChaostrack,
        maturityTarget,
        maturityTargetLabel,
        departments,
        kpiPrivacy,
        partnerGrantableEntitlements,
        expectedUpdated,
      }) => {
        const accessToken = await getAccessTokenSilently()
        return await updateOrg(accessToken, user?.sub, {
          id,
          displayName,
          sponsorName,
          sponsorEmail,
          variables,
          logoURL,
          brandColor,
          gameplayPreferences,
          companyURL,
          entitlements,
          archived,
          managingOrgId,
          isPartnerOrg,
          inheritPartnerBranding,
          showManagedByLabel,
          showPoweredByChaostrack,
          maturityTarget,
          maturityTargetLabel,
          departments,
          kpiPrivacy,
          // Platform admin only: what this partner may grant its clients. Was dropped here (and in
          // updateOrg) so the box never saved and snapped back on reopen.
          partnerGrantableEntitlements,
          expectedUpdated,
        })
      },
      onSuccess: (result, { id, departments }) => {
        // KPI P1 (M17): the Settings cards share the HOME_USER_ORG entry and each save sends
        // `updated` as its concurrency token. Write the saved fields and the fresh token into the
        // cache FIRST, so a second card saved before the refetch lands doesn't 409 on its own
        // earlier save. Only these fields — the response is the full org doc, heavier than getOrg.
        if (result && result.id === id) {
          const fresh = {}
          for (const key of ["departments", "kpiPrivacy", "updated"]) if (key in result) fresh[key] = result[key]
          queryClient.setQueriesData({ queryKey: ["HOME_USER_ORG"] }, (old) => (old && old.id === id ? { ...old, ...fresh } : old))
        }
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS_LIST(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_ORGS(user?.sub) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, id) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_DETAIL(user?.sub, id) })
        // Shared org cache (app shell + Settings surface): org edits — branding, maturity
        // target, preferences — must show up everywhere without waiting for the 30s poll.
        queryClient.invalidateQueries({ queryKey: ["HOME_USER_ORG"] })
        // KPI P1: department and comparison-group edits change Data coverage (untagged counts per
        // group, people per department), so its cached counts are stale after such a save.
        if (departments !== undefined) queryClient.invalidateQueries({ queryKey: ["KPI_COVERAGE"] })
      },
    })
  },

  // Update sponsor details including orgId (needed for partner child-org block/unblock).
  //
  // ITF-01: the sponsors table on OrganizationDetail reads ORG_STATS.sponsors, whose
  // `blocked` flag is sourced from Auth0's users?q= search index (see server
  // getOrgMembersWithRoles). That index is eventually-consistent, so a refetch fired
  // immediately after the block PATCH frequently returns the pre-block value — which is
  // why the block toggle appeared to need a second click. We optimistically patch the
  // cached ORG_STATS so the row flips instantly, then still invalidate so the server
  // value reconciles once the index catches up (onSettled). onError rolls back.
  UPDATE_SPONSOR: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ targetUserId, name, blocked, orgId }) => {
        const accessToken = await getAccessTokenSilently()
        return await updateSponsor(accessToken, user?.sub, { targetUserId, name, blocked, orgId })
      },
      onMutate: async ({ targetUserId, blocked, orgId }) => {
        // Only the block/unblock toggle carries a boolean `blocked`; skip for name-only edits.
        if (!orgId || typeof blocked !== "boolean") return {}
        const statsKey = ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, orgId)
        await queryClient.cancelQueries({ queryKey: statsKey })
        const previousStats = queryClient.getQueryData(statsKey)
        if (previousStats && Array.isArray(previousStats.sponsors)) {
          queryClient.setQueryData(statsKey, {
            ...previousStats,
            sponsors: previousStats.sponsors.map((s) => (s.userId === targetUserId ? { ...s, blocked } : s)),
          })
        }
        return { statsKey, previousStats }
      },
      onError: (_err, _vars, context) => {
        // Roll the optimistic patch back on failure so a rejected block doesn't leave the
        // row showing the wrong state.
        if (context?.statsKey && context?.previousStats !== undefined) {
          queryClient.setQueryData(context.statsKey, context.previousStats)
        }
      },
      onSettled: (_data, _err, { orgId }) => {
        if (orgId) {
          queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, orgId) })
          queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_MEMBERS(user?.sub, orgId) })
        }
      },
    })
  },

  // Update scenario org-access using a delta (add/remove). Throws on ≥400.
  UPDATE_SCENARIO_ORG_ACCESS: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ name, orgAccessAdd, orgAccessRemove }) => {
        const accessToken = await getAccessTokenSilently()
        return await updateScenarioOrgAccess(accessToken, user?.sub, { name, orgAccessAdd, orgAccessRemove })
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.MANAGED_SCENARIOS(user?.sub) })
      },
    })
  },

  // Member admin mutations — wrap existing data.js functions.
  //
  // ITF-01: like the block toggle, a freshly-invited sponsor does not show up on a
  // refetch fired right after the invite because ORG_STATS.sponsors is sourced from
  // Auth0's eventually-consistent users?q= search index. The invite handler returns the
  // created Auth0 user, so on success we optimistically splice that sponsor into the
  // cached ORG_STATS (and bump the sponsor count) — it appears immediately — then still
  // invalidate so the authoritative server list reconciles once the index catches up.
  INVITE_SPONSOR: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ orgId, name, email, force = false }) => {
        const accessToken = await getAccessTokenSilently()
        return await inviteSponsor(accessToken, user?.sub, { orgId, name, email, force })
      },
      onSuccess: (data, { orgId, name, email }) => {
        // data is { result, message }; result is the created Auth0 user on success.
        const createdUser = data?.result ?? null
        // CT-1096/#3: a promote that needs sponsor confirmation returns { needsConfirmation }
        // (no user created) — do NOT optimistically add anything for it.
        if (createdUser && !createdUser.needsConfirmation && orgId) {
          const statsKey = ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, orgId)
          const previousStats = queryClient.getQueryData(statsKey)
          if (previousStats && Array.isArray(previousStats.sponsors)) {
            const newUserId = createdUser.user_id ?? createdUser.userId
            const alreadyListed = newUserId && previousStats.sponsors.some((s) => s.userId === newUserId)
            // Only splice a row when the server returned a real created user (has a user_id).
            // A needsConfirmation/partial payload has none — never invent a pending- row.
            if (newUserId && !alreadyListed) {
              const newSponsor = {
                userId: newUserId,
                name: createdUser.name ?? name ?? null,
                email: createdUser.email ?? email ?? null,
                loginsCount: createdUser.logins_count ?? 0,
                lastLogin: createdUser.last_login ?? null,
                blocked: createdUser.blocked === true,
              }
              queryClient.setQueryData(statsKey, {
                ...previousStats,
                sponsors: [...previousStats.sponsors, newSponsor],
                byRole: { ...(previousStats.byRole ?? {}), sponsor: (previousStats.byRole?.sponsor ?? previousStats.sponsors.length) + 1 },
                totalUsers: (previousStats.totalUsers ?? previousStats.sponsors.length) + 1,
              })
            }
          }
        }
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, orgId) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_MEMBERS(user?.sub, orgId) })
      },
    })
  },

  INVITE_ORG_TRAINER: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ orgId, name, email }) => {
        const accessToken = await getAccessTokenSilently()
        return await inviteOrgTrainer(accessToken, user?.sub, { orgId, name, email })
      },
      onSuccess: (_, { orgId }) => {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_MEMBERS(user?.sub, orgId) })
      },
    })
  },

  INVITE_ORG_USER: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ orgId, name, email }) => {
        const accessToken = await getAccessTokenSilently()
        return await inviteOrgUser(accessToken, user?.sub, { orgId, name, email })
      },
      onSuccess: (_, { orgId }) => {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_MEMBERS(user?.sub, orgId) })
      },
    })
  },

  DELETE_ORG_MEMBER: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ orgId, targetUserId }) => {
        const accessToken = await getAccessTokenSilently()
        return await deleteOrgMember(accessToken, user?.sub, { orgId, targetUserId })
      },
      onSuccess: (_, { orgId }) => {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_MEMBERS(user?.sub, orgId) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, orgId) })
      },
    })
  },

  RESTORE_ORG_MEMBER: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ orgId, targetUserId }) => {
        const accessToken = await getAccessTokenSilently()
        return await restoreOrgMember(accessToken, user?.sub, { orgId, targetUserId })
      },
      onSuccess: (_, { orgId }) => {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_MEMBERS(user?.sub, orgId) })
      },
    })
  },

  RESEND_MEMBER_INVITE: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ orgId, targetUserId }) => {
        const accessToken = await getAccessTokenSilently()
        return await resendMemberInvite(accessToken, user?.sub, { orgId, targetUserId })
      },
    })
  },

  TRANSFER_PRIMARY_SPONSORSHIP: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ orgId, toUserId }) => {
        const accessToken = await getAccessTokenSilently()
        return await transferPrimarySponsorship(accessToken, user?.sub, { orgId, toUserId })
      },
      onSuccess: (_, { orgId }) => {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_STATS(user?.sub, orgId) })
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.ORG_MEMBERS(user?.sub, orgId) })
      },
    })
  },

  ORG_MEMBERS: (orgId, queryParams = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: [...ORGANIZATION_QUERY_KEYS.ORG_MEMBERS(user?.sub, orgId), queryParams],
      queryFn: async () => {
        const accessToken = await getAccessTokenSilently()
        return await getOrgMembers(accessToken, user?.sub, { orgId, ...queryParams })
      },
      enabled: !!user?.sub && !!orgId,
      staleTime: 30000,
    })
  },

  // `enabled` accepts an optional caller override so a page guard (e.g. canManageOrgs)
  // can prevent the fetch from firing before the access check passes.
  ORG_DETAIL: (orgId, { enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: ORGANIZATION_QUERY_KEYS.ORG_DETAIL(user?.sub, orgId),
      queryFn: async () => {
        const accessToken = await getAccessTokenSilently()
        return await getOrg(accessToken, user?.sub, { id: orgId })
      },
      enabled: !!user?.sub && !!orgId && enabled,
      staleTime: 30000,
    })
  },

  // Returns the actor's managed org IDs (archived-INCLUSIVE) for D6 edit gating.
  // Source: /managed-orgs for sponsor/manager; /orgs for trainer (trainer-permitted).
  // Use activeManagedOrgs (archived !== true filter) for org-selector options.
  MANAGED_ORG_IDS: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    const trainerMode = isTrainer(user)
    return useQuery({
      queryKey: [...ORGANIZATION_QUERY_KEYS.MANAGED_ORG_IDS(user?.sub), trainerMode],
      queryFn: async () => {
        const accessToken = await getAccessTokenSilently()
        const orgs = trainerMode ? ((await getOrgs(accessToken, user?.sub)) ?? []) : ((await getManagedOrgs(accessToken, user?.sub)) ?? [])
        return orgs.map((o) => o.id)
      },
      enabled: !!user?.sub,
      staleTime: 30000,
    })
  },
})

/**
 * Selector hook: returns the actor's managed org-ID set (archived-INCLUDING).
 * For D6 canEditScenarioClient gating — a partner may still edit a scenario owned
 * by a now-archived child so archived orgs must be included here.
 *
 * Source: /managed-orgs for sponsor/manager; /orgs for trainer.
 */
export const useManagedOrgIds = () => {
  const { data: orgIds = [], ...rest } = useOrganizationApis.MANAGED_ORG_IDS()
  return { managedOrgIds: orgIds, ...rest }
}

/**
 * Selector hook: returns the ACTIVE (non-archived) managed orgs for selectors.
 * Use for org-access delta multi-select and add-exercise org selector — the backend
 * scenario delta scope is archived-EXCLUDING and exercise creation rejects archived orgs.
 *
 * Source: same as MANAGED_ORG_IDS but returns full org objects filtered to archived !== true.
 */
export const useActiveManagedOrgs = () => {
  const { getAccessTokenSilently, user } = useAuth0()
  const trainerMode = isTrainer(user)
  const { data: allOrgs = [], ...rest } = useQuery({
    queryKey: [...ORGANIZATION_QUERY_KEYS.MANAGED_ORG_IDS(user?.sub), trainerMode, "active-orgs"],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return trainerMode ? ((await getOrgs(accessToken, user?.sub)) ?? []) : ((await getManagedOrgs(accessToken, user?.sub)) ?? [])
    },
    enabled: !!user?.sub,
    staleTime: 30000,
  })
  const activeManagedOrgs = allOrgs.filter((o) => o.archived !== true)
  return { activeManagedOrgs, ...rest }
}
